/*
 * Super Admin → Normalize Risk Bands (Master-Prompt Stage B-BE).
 * Controlled dry-run -> apply of the 3-band (80/60) label normalization on the
 * live DB. Scores are never changed — only stale 5-band labels in stored
 * snapshots are recomputed. Safe to run on production after deploy.
 */
import React, { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { Loader2, Layers, PlayCircle, CheckCircle2, AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function RiskBandsPage({ session }) {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [applied, setApplied] = useState(false);
  const [excludeLeona, setExcludeLeona] = useState(false);
  const headers = {
    "x-user-role": session?.user?.role || "admin",
    "x-user-id": session?.user?.id || "",
  };

  const run = async (dryRun) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/admin/risk-bands/migrate?dry_run=${dryRun}&exclude_leona=${excludeLeona}`, {}, { headers });
      setResult(r.data);
      setApplied(!dryRun);
      if (!dryRun) {
        toast.success(`Normalized ${r.data.total_fields_changed} labels across ${r.data.total_docs_affected} documents`);
      } else {
        toast.success(r.data.total_fields_changed === 0 ? "Already normalized — nothing to change" : `Dry-run: ${r.data.total_fields_changed} labels in ${r.data.total_docs_affected} docs would change`);
      }
    } catch {
      toast.error("Migration request failed");
    } finally {
      setBusy(false);
    }
  };

  const nothingToDo = result && result.total_fields_changed === 0;

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8" data-testid="risk-bands-page">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-[10px] text-white" style={{ backgroundColor: "#007a9e" }}>
          <Layers className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#111827]">Normalize Risk Bands</h1>
          <p className="text-sm text-[#6b7280] mt-0.5">Recompute stored snapshot labels to the 3-band scheme (Healthy ≥ 80 · Caution 60–79 · High Risk &lt; 60). Scores are never changed.</p>
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-[#bfe3ee] bg-[#e6f4f8]/60 p-4 text-[13px] text-[#00607d] flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <span>Always run <strong>Dry run</strong> first to preview exactly how many documents and labels will change. Only run <strong>Apply</strong> once the preview looks right. The operation is idempotent — running it again when everything is already normalized changes nothing.</span>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <input
          type="checkbox"
          id="exclude-leona"
          checked={excludeLeona}
          onChange={(e) => setExcludeLeona(e.target.checked)}
          className="h-4 w-4 rounded border-[#cbd5e1] text-[#007a9e] focus:ring-[#007a9e]"
          data-testid="risk-bands-exclude-leona"
        />
        <label htmlFor="exclude-leona" className="text-[13px] text-[#374151]">
          Exclude <strong>The Leona Group</strong> (live customer) from this run
        </label>
      </div>

      <div className="mt-4 flex flex-wrap gap-3">
        <Button onClick={() => run(true)} disabled={busy} variant="outline" className="border-[#007a9e] text-[#00698a] hover:bg-[#e6f4f8]" data-testid="risk-bands-dry-run">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PlayCircle className="h-4 w-4 mr-2" />}
          Dry run (preview)
        </Button>
        <Button
          onClick={() => run(false)}
          disabled={busy || !result || nothingToDo}
          className="bg-[#007a9e] hover:bg-[#00607d] text-white disabled:opacity-50"
          data-testid="risk-bands-apply"
          title={!result ? "Run a dry-run first" : nothingToDo ? "Nothing to change" : ""}
        >
          <CheckCircle2 className="h-4 w-4 mr-2" />
          Apply changes
        </Button>
      </div>

      {result && (
        <div className="mt-6 rounded-xl border border-[#e5e7eb] bg-white p-5" data-testid="risk-bands-result">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-[15px] font-bold text-[#111827]">
              {applied ? "Applied" : "Dry-run preview"}
            </h2>
            <span className={`text-[11.5px] font-bold uppercase tracking-wide px-2 py-0.5 rounded ${applied ? "bg-[#f0fdf4] text-[#166534] border border-[#bbf7d0]" : "bg-[#e6f4f8] text-[#00698a] border border-[#bfe3ee]"}`}>
              {applied ? "Written to DB" : "Read-only"}
            </span>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-3">
            <div className="rounded-lg border border-[#e5e7eb] p-3"><div className="text-2xl font-bold text-[#007a9e]" data-testid="rb-docs">{result.total_docs_affected}</div><div className="text-[11.5px] text-[#6b7280] mt-0.5">Documents</div></div>
            <div className="rounded-lg border border-[#e5e7eb] p-3"><div className="text-2xl font-bold text-[#007a9e]" data-testid="rb-fields">{result.total_fields_changed}</div><div className="text-[11.5px] text-[#6b7280] mt-0.5">Labels</div></div>
            <div className="rounded-lg border border-[#e5e7eb] p-3"><div className="text-2xl font-bold text-[#007a9e]">{result.distinct_transitions}</div><div className="text-[11.5px] text-[#6b7280] mt-0.5">Transitions</div></div>
          </div>

          {(applied && result.backup_id) && (
            <p className="mt-3 text-[12px] text-[#166534] flex items-center gap-1.5" data-testid="rb-backup">
              <CheckCircle2 className="h-3.5 w-3.5" /> Pre-apply backup saved: <code className="font-mono text-[#0f172a]">{result.backup_id}</code>
            </p>
          )}
          {result.excluded_tenant_count > 0 && (
            <p className="mt-2 text-[12px] text-[#92400e]" data-testid="rb-excluded">
              Leona Group excluded: {result.excluded_tenant_count} tenant(s) skipped this run.
            </p>
          )}

          {nothingToDo ? (
            <p className="mt-4 text-[13px] text-[#166534] flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Everything is already on the 3-band scheme. No changes needed.</p>
          ) : (
            <>
              {(result.transitions || []).length > 0 && (
                <div className="mt-4">
                  <div className="text-[11.5px] font-semibold uppercase tracking-wide text-[#6b7280] mb-1.5">Label changes</div>
                  <ul className="space-y-1">
                    {result.transitions.map((t, i) => (
                      <li key={i} className="text-[13px] text-[#374151] flex items-center gap-2">
                        <span className="inline-block min-w-[40px] text-right font-bold text-[#007a9e]">{t.count}×</span>
                        <span className="text-[#6b7280] line-through">{t.from}</span>
                        <span className="text-[#9ca3af]">→</span>
                        <span className="font-semibold text-[#111827]">{t.to}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-[12px]">
                  <thead><tr className="text-left text-[#6b7280] border-b border-[#e5e7eb]"><th className="py-1.5 pr-3 font-semibold">Collection</th><th className="py-1.5 pr-3 font-semibold text-right">Docs</th><th className="py-1.5 font-semibold text-right">Labels</th></tr></thead>
                  <tbody>
                    {(result.collections || []).map((c) => (
                      <tr key={c.collection} className="border-b border-[#f1f5f9]">
                        <td className="py-1.5 pr-3 text-[#374151]">{c.collection}</td>
                        <td className="py-1.5 pr-3 text-right text-[#111827] font-medium">{c.docs_affected}</td>
                        <td className="py-1.5 text-right text-[#111827] font-medium">{c.fields_changed}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
